import dayjs from "@lib/dayjs";
import {
  getSlotStatus,
  MeetingParties,
  TypeGroupOfProposals,
} from "../MeetingPollDetailPage";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import { useMemo, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { sendInterviewInvite } from "@endpoints/interview.service";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

interface PastProposalCardProps {
  index: number;
  interviewId: string;
  proposal: TypeGroupOfProposals;
  isHiringCompanyInvolved: boolean;
  isLatestProposalByMe: boolean;
  isInviteSend: boolean;
  isEverySlotsDeclined: boolean;
  isAssignedHiringTeamMember?: boolean;
}

const PastProposalCard = (props: PastProposalCardProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    proposal,
    isHiringCompanyInvolved,
    isLatestProposalByMe,
    index,
    interviewId,
    isInviteSend,
    isEverySlotsDeclined,
    isAssignedHiringTeamMember,
  } = props;
  const user: any = useSelector((state: RootState) => state.auth.user);

  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const proposedBy = proposal?.slots[0]?.proposedBy;
  const isSelfProposedSlots =
    proposedBy?._id ===
    (user?.userType === userTypes.RECRUITER ||
    user?.userType === userTypes.HIRING_COMPANY
      ? user?._id
      : user?.userId);
  const meetingResponder = useMemo(() => {
    const parties = isHiringCompanyInvolved
      ? MeetingParties
      : [EnumUserTypes.CANDIDATE, EnumUserTypes.RECRUITER];
    return parties.filter((party) => proposedBy?.userType !== party);
  }, [isHiringCompanyInvolved, proposedBy]);

  const handleSendInvite = async (slotId: string) => {
    try {
      setIsSendingInvite(true);
      const { success } = await sendInterviewInvite({
        id: interviewId,
        slotId,
      });
      if (success)
        enqueueSnackbar("Invitation sent successfully.", {
          variant: "success",
        });
      navigate("/calendar/meeting/scheduling");
    } catch (error) {
      enqueueSnackbar("Error sending interview invite", { variant: "error" });
      console.error("Error: sending interview invite", error);
    } finally {
      setIsSendingInvite(false);
    }
  };

  return (
    <div
      key={proposal?.createdAt}
      className="mt-8 py-4 gap-10 bg-gray-50 rounded-md px-5"
    >
      <div className="text-xs font-medium italic text-light-black mb-4">
        Submitted at{" "}
        {dayjs(proposal?.slots[0]?.createdAt).format("dddd Do MMM HH:mm")}
      </div>
      <div className="grid grid-cols-[1fr_0.6fr_0.6fr]">
        {/* Column 1 */}
        <div className="flex flex-col gap-5">
          <span className="font-semibold text-xs text-primary">
            {isSelfProposedSlots
              ? "Your"
              : proposedBy?.userType === userTypes.CANDIDATE
              ? "Candidate's"
              : proposedBy?.userType === userTypes.HIRING_COMPANY
              ? "Hiring Company's"
              : "Agent's"}{" "}
            proposed times
          </span>
          <div className="flex flex-col gap-4">
            {proposal?.slots?.map((slot) => {
              const date = dayjs(slot?.startTime).format("dddd MMM Do");
              const showSendInvite = useMemo(
                () =>
                  slot?.isCandidateAvailable &&
                  slot?.isRecruiterAvailable &&
                  slot?.isCompanyAvailable &&
                  (!slot?.hiringTeamAvailability?.length ||
                    slot?.hiringTeamAvailability?.every(
                      (availability) => availability?.isAvailable === true
                    )) &&
                  user?.userType === EnumUserTypes?.RECRUITER &&
                  isLatestProposalByMe &&
                  index === 0,
                [slot, user]
              );
              return (
                <div
                  key={slot?._id}
                  className="items-center grid grid-cols-[1fr_0.7fr_0.7fr]"
                >
                  <span className="text-xs font-medium text-light-black">
                    {date}
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {dayjs(slot?.startTime).format("HH:mm")}
                    &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                    {dayjs(slot?.endTime).format("HH:mm")}
                  </span>
                  {showSendInvite ? (
                    <button
                      disabled={isSendingInvite || isInviteSend}
                      onClick={() => handleSendInvite(slot?._id)}
                      className={`flex items-center justify-center gap-2 bg-primary text-white border text-xs font-semibold rounded-md py-0.5 px-1.5 w-max`}
                    >
                      {isInviteSend ? "Invited" : "Send Invite"}
                      {isSendingInvite ? (
                        <LoadingAnimation className="w-4 h-4" />
                      ) : null}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        {/* Column 2 */}
        {isHiringCompanyInvolved ? (
          <div className="flex flex-col gap-5 text-center">
            <span className="font-semibold text-xs text-primary">
              {meetingResponder[1] === EnumUserTypes.CANDIDATE
                ? "Candidate's"
                : meetingResponder[1] === EnumUserTypes.HIRING_COMPANY
                ? "Hiring company's"
                : "Agent's"}{" "}
              response
            </span>
            <div className="flex flex-col gap-4">
              {proposal?.slots?.map((slot) => {
                return (
                  <span
                    key={slot?._id}
                    className="text-xs capitalize text-primary"
                  >
                    {getSlotStatus({
                      slot,
                      statusOf: meetingResponder[1],
                      isAssignedHiringTeamMember,
                    })}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}
        {/* Column 3 */}
        <div className="flex flex-col gap-5 text-center">
          <span className="font-semibold text-xs text-primary">
            {meetingResponder[0] === EnumUserTypes.RECRUITER
              ? "Agent's"
              : meetingResponder[0] === EnumUserTypes.HIRING_COMPANY
              ? "Hiring company's"
              : "Candidate's"}{" "}
            response
          </span>
          <div className="flex flex-col gap-4">
            {proposal?.slots?.map((slot) => {
              return (
                <span
                  key={slot?._id}
                  className="text-xs capitalize text-primary"
                >
                  {getSlotStatus({
                    slot,
                    statusOf: meetingResponder[0],
                    isAssignedHiringTeamMember,
                  })}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastProposalCard;
